import { Icon } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { DateTimeDisplay } from "@/components/pieces/DateTimeDisplay";
import { LIGHT_GRAY } from "@/css/constants";
import { ProgramStatus } from "@/graphql";

import { LiveSVG, PauseSVG } from "./ProgramStatusIcons";

const STATUS_CONFIG: Record<
  ProgramStatus,
  {
    displayName: string;
    color: string;
    bg: string;
    icon: React.ReactNode;
    targetDateText: string;
  }
> = {
  [ProgramStatus.InDesign]: {
    displayName: "In Design",
    color: "#BF7326",
    bg: "#FBF2E9",
    icon: <Icon icon="circle" size={14} />,
    targetDateText: "Target Launch",
  },
  [ProgramStatus.Pause]: {
    displayName: "Paused",
    color: "#BF7326",
    bg: "#FBF2E9",
    icon: <PauseSVG />,
    targetDateText: "",
  },
  [ProgramStatus.Complete]: {
    displayName: "Complete",
    color: "#0D8050",
    bg: "#E6F5EF",
    icon: <Icon icon="tick-circle" size={14} />,
    targetDateText: "",
  },
  [ProgramStatus.Live]: {
    displayName: "Live",
    color: "#215DB0",
    bg: "#E9F1FB",
    icon: <LiveSVG />,
    targetDateText: "",
  },
  [ProgramStatus.LaunchReady]: {
    displayName: "Launch Ready",
    color: "#215DB0",
    bg: "#E9F1FB",
    icon: <LiveSVG />,
    targetDateText: "Launching",
  },
  [ProgramStatus.Cancelled]: {
    displayName: "Canceled",
    color: "#C61D1D",
    bg: "#F5E6E8",
    icon: <Icon icon="cross-circle" size={14} />,
    targetDateText: "",
  },
  [ProgramStatus.Archived]: {
    displayName: "Archived",
    color: "#778397",
    bg: LIGHT_GRAY,
    icon: <Icon icon="full-circle" size={14} />,
    targetDateText: "",
  },
  [ProgramStatus.Failed]: {
    displayName: "Failed",
    color: "#C61D1D",
    bg: "#F5E6E8",
    icon: <Icon icon="cross-circle" size={14} />,
    targetDateText: "",
  },
  [ProgramStatus.DidNotPass]: {
    displayName: "Did Not Pass",
    color: "#C61D1D",
    bg: "#F5E6E8",
    icon: <Icon icon="cross-circle" size={14} />,
    targetDateText: "",
  },
  [ProgramStatus.DidNotFinish]: {
    displayName: "Did Not Finish",
    color: "#C61D1D",
    bg: "#F5E6E8",
    icon: <Icon icon="cross-circle" size={14} />,
    targetDateText: "",
  },
};

interface ProgramDateDisplayProps {
  status: ProgramStatus;
  targetLaunchDate?: string | null;
  targetEndDate?: string | null;
  closedAt?: string | null;
}

export function ProgramDateDisplay({
  status = ProgramStatus.Archived,
  targetLaunchDate,
  targetEndDate,
  closedAt,
}: ProgramDateDisplayProps) {
  const { targetDateText } = STATUS_CONFIG[status];

  const showEndDate = [
    ProgramStatus.Live,
    ProgramStatus.Complete,
    ProgramStatus.Pause,
    ProgramStatus.Cancelled,
  ].includes(status);

  const endDate = targetEndDate || closedAt;

  return (
    <>
      {targetDateText ? `${targetDateText} ` : ""}
      {targetLaunchDate && (
        <DateTimeDisplay time={targetLaunchDate} formatString="MMM d, yyyy" />
      )}
      {showEndDate && endDate && (
        <>
          {" - "}
          <DateTimeDisplay time={endDate} formatString="MMM d, yyyy" />
        </>
      )}
    </>
  );
}

export function ProgramStatusBadge({ status }: { status: ProgramStatus }) {
  const { color, bg, icon, displayName } = STATUS_CONFIG[status];

  return (
    <Flex alignItems="center">
      <Flex
        py="2px"
        px={1}
        letterSpacing={"0.3px"}
        bg={bg}
        alignItems="center"
        fontSize={14}
        fontWeight={500}
        color={color}
        css={{
          borderRadius: 5,
          textTransform: "capitalize",
        }}
      >
        <Flex
          alignItems="center"
          justifyContent="center"
          width={20}
          height={20}
          mr={1}
        >
          {icon}
        </Flex>
        <Box className="xs:text-xs md:text-sm" css={{ textWrap: "nowrap" }}>
          {displayName}
        </Box>
      </Flex>
    </Flex>
  );
}
